import './measure.css';

import VectorSource from 'ol/source/Vector';
import Vector from 'ol/layer/Vector';
import Overlay from 'ol/Overlay';
import { unByKey as unByKeyOl } from 'ol/Observable';
import { getArea, getLength } from 'ol/sphere';
import Polygon from 'ol/geom/Polygon';
import LineString from 'ol/geom/LineString';
import h from 'hyperscript';

import { MapProperty } from 'cadenza/map/map-property';
import { DrawInteraction } from 'cadenza/map/interactions/draw-interaction';
import { formatArea, formatLength } from 'cadenza/utils/format/format';
import { on, unByKey } from 'cadenza/utils/event-util';

import i18n from './interactions.properties';

/**
 * Vertical offset in pixels relative to the point position for the static value overlay shown
 * when measuring is finished.
 *
 * @type {number}
 */
const TOOLTIP_STATIC_Y_OFFSET = -7;

/**
 * Vertical offset in pixels relative to the point position for the tooltip that shows the current
 * value while measuring.
 *
 * @type {number}
 */
const TOOLTIP_Y_OFFSET = 15;

/**
 * Interaction to do measurements on the map.
 * Depending on the geometry type passed on creation (LineString or Polygon) it will measure the
 * distance between the points in a line, or the area of a polygon.
 * The user adds points by clicking on the map.
 * The value measured is shown on a tooltip in a map overlay.
 */
export class Measure extends DrawInteraction {

  #geometryEventKey;
  #tooltipEventKey;
  #map;
  #measureTooltipElement;
  #measureTooltip;
  #vectorLayer;

  /**
   * Constructor
   *
   * @param {ol.Map} map - The map on which the interaction performs
   * @param {'LineString'|'Polygon'} type - The type of geometry to measure
   */
  constructor (map, type) {
    const source = new VectorSource();
    super({ type, source, style: map.get(MapProperty.DRAW_STYLES)[type] }, map);

    this.#map = map;
    this.#measureTooltipElement = null;
    this.#measureTooltip = null;

    this.#vectorLayer = new Vector({
      map,
      source,
      style: map.get(MapProperty.DEFAULT_STYLES)[type]
    });

    this._onDrawStartHandler = event => { this._onDrawStart(event); };
    this._onDrawEndHandler = event => { this._onDrawEnd(event); };
    this._onDrawAbortHandler = event => { this._onDrawAbort(event); };

    this.on('change:active', event => {
      if (!event.oldValue) {
        this._onActivation();
      } else {
        this._onDeactivation();
      }
    });
  }

  /**
   * Get help message for tooltip
   *
   * @override
   * @param geometry - The geometry
   */
  getHelpMessage (geometry) {
    return i18n(geometry ? 'measure.distance.continue' : 'measure.distance.start');
  }

  _onActivation () {
    this._reset();
    this.#vectorLayer.setMap(this.#map);

    this.on('drawstart', this._onDrawStartHandler);
    this.on('drawend', this._onDrawEndHandler);
    this.on('drawabort', this._onDrawAbortHandler);

    this.#map.getViewport().addEventListener('mouseout', this._onMouseOutHandler);
  }

  _onDeactivation () {
    this.#vectorLayer.setMap(null);
    this._removeMeasureTooltip();

    this.un('drawstart', this._onDrawStartHandler);
    this.un('drawend', this._onDrawEndHandler);
    this.un('drawabort', this._onDrawAbortHandler);

    this.#map.getViewport().removeEventListener('mouseout', this._onMouseOutHandler);
  }

  _onDrawStart (event) {
    this._reset();
    this._bindDrawingListeners(event);
  }

  _onDrawEnd () {
    this.#measureTooltip.setPositioning('bottom-center');
    this.#measureTooltip.setOffset([ 0, TOOLTIP_STATIC_Y_OFFSET ]);
    this.#measureTooltipElement = null;
    this._unBindDrawingListeners();
  }

  _bindDrawingListeners (event) {
    this.#geometryEventKey = event.feature.getGeometry()
      .on('change', event => this._onGeometryChanged(event));

    this.#tooltipEventKey = on(
      this.#measureTooltipElement,
      'mouseenter',
      event => this._toggleTooltipPositioning(event)
    );
  }

  _unBindDrawingListeners () {
    unByKeyOl(this.#geometryEventKey);
    unByKey(this.#tooltipEventKey);
  }

  _onGeometryChanged (event) {
    const geom = event.target;
    const projection = this.#map.getView().getProjection();
    let output;
    let tooltipCoord;
    if (geom instanceof Polygon) {
      const hasArea = geom.getCoordinates()[0].length > 3;
      output = formatArea(hasArea ? getArea(geom, { projection }) : 0);
      tooltipCoord = geom.getInteriorPoint().getCoordinates();
    } else if (geom instanceof LineString) {
      output = formatLength(getLength(geom, { projection }));
      tooltipCoord = geom.getLastCoordinate();
    }
    this.#measureTooltipElement.textContent = output;
    this.#measureTooltip.setPosition(tooltipCoord);
  }

  _reset () {
    this.#vectorLayer.getSource().clear(true);
    this._removeMeasureTooltip();
    this._createMeasureTooltip();
  }

  /**
   * Toogles the tooltip positioning to avoid it interfering with the mouse cursor when measuring
   * area. The positioning alternates between above and under the coordinates.
   * The arrow styling and offset are also adapted accordingly.
   *
   * @private
   */
  _toggleTooltipPositioning () {
    this.#measureTooltipElement.classList.toggle('tooltip-arrow-below');
    this.#measureTooltipElement.classList.toggle('tooltip-arrow-above');

    if (this.#measureTooltip.getPositioning() === 'bottom-center') {
      this.#measureTooltip.setPositioning('top-center');
      this.#measureTooltip.setOffset([ 0, TOOLTIP_Y_OFFSET ]);
    } else {
      this.#measureTooltip.setPositioning('bottom-center');
      this.#measureTooltip.setOffset([ 0, -TOOLTIP_Y_OFFSET ]);
    }
  }

  _onDrawAbort () {
    this._removeMeasureTooltip();
    this._unBindDrawingListeners();
  }

  _createMeasureTooltip () {
    if (this.#measureTooltipElement) {
      this.#measureTooltipElement.remove();
    }
    this.#measureTooltipElement = h('.tooltip.tooltip--with-arrow.tooltip-arrow-below.tooltip--in-progress');
    this.#measureTooltip = new Overlay({
      element: this.#measureTooltipElement,
      offset: [ 0, -15 ],
      positioning: 'bottom-center'
    });
    this.#map.addOverlay(this.#measureTooltip);
    return this.#measureTooltip;
  }

  _removeMeasureTooltip () {
    if (this.#measureTooltipElement) {
      this.#measureTooltipElement.remove();
      this.#measureTooltipElement = null;
    }
    if (this.#measureTooltip) {
      this.#map.removeOverlay(this.#measureTooltip);
      this.#measureTooltip = null;
    }
  }

}
